import {
  type ConvertibleUnit,
  format,
  type FormatOptions,
  type Unit,
} from '@dynatrace-sdk/units';

import { defaultUnitFormatter } from './defaultUnitFormatter.js';
import { getFormatterPrecision } from './format-value.js';
import type { ChartUnit } from '../types/chart-unit.js';
import {
  type Formatter,
  type FormatterWithPrecision,
  isFormatterOptions,
} from '../types/formatter.js';

/** @internal */
export const getTickFormatterMethod = (
  values: number[],
  formatter?: Formatter | FormatOptions<Unit, ConvertibleUnit>,
  unit?: ChartUnit,
) => {
  let tickFormatter: FormatterWithPrecision;

  const isDefaultFormatter = !formatter;
  const isCustomFormatter = !isFormatterOptions(formatter);
  const isCustomFormatterOrHasPrecisionDefined =
    isCustomFormatter ||
    formatter.maximumFractionDigits ||
    formatter.minimumFractionDigits;

  if (isDefaultFormatter) {
    const precisionFormatter = (
      value: number,
      unit?: ChartUnit,
      precision?: number,
    ) => defaultUnitFormatter(unit, precision)(value);

    const precision = getFormatterPrecision(values, precisionFormatter, unit);
    tickFormatter = defaultUnitFormatter(unit, precision);
  } else if (isCustomFormatterOrHasPrecisionDefined) {
    tickFormatter = isCustomFormatter
      ? formatter
      : (value: number) => format(value, formatter);
  } else {
    const precisionFormatter = (
      value: number,
      unit?: ChartUnit,
      precision?: number,
    ) =>
      format(value, {
        maximumFractionDigits: precision,
        minimumFractionDigits: precision,
        ...formatter,
      });
    const precision = getFormatterPrecision(values, precisionFormatter, unit);

    tickFormatter = (value: number) =>
      format(value, {
        maximumFractionDigits: precision,
        ...formatter,
      });
  }

  return tickFormatter;
};
